<template>
  <div class="relative inline-flex items-center gap-x-2 h-fit" data-testid="language-switcher">
    <Button
      type="button"
      variant="outlineSoft"
      size="sm"
      class="py-2 px-2 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-800 shadow-2xs hover:bg-gray-50 focus:outline-hidden focus:bg-gray-50"
      :aria-label="t('vde.languages.dropdown.aria.label')"
      :aria-expanded="dropdownOpen"
      @click="dropdownOpen = !dropdownOpen"
      @blur="closeDropdown"
    >
      <span class="text-lg">{{ selectedLanguage.flag }}</span>
      <span class="font-medium">{{ selectedLanguage.name }}</span>
      <svg
        :class="['size-4 transition-transform', dropdownOpen ? 'rotate-180' : '']"
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="m6 9 6 6 6-6" />
      </svg>
    </Button>

    <div
      v-show="dropdownOpen"
      class="absolute right-0 bottom-full mb-2 lg:bottom-auto lg:top-full lg:mt-2 z-[60] bg-white shadow-md rounded-lg"
      role="menu"
    >
      <div class="p-1 space-y-0.5">
        <Button
          v-for="lang in languages"
          :key="lang.code"
          type="button"
          variant="ghost"
          :disabled="lang.code === currentLocale"
          :class="[
            'flex items-center gap-x-2 py-2 px-3 w-full rounded-lg text-sm text-gray-800 hover:bg-gray-100 focus:outline-hidden focus:bg-gray-100 transition',
            lang.code === currentLocale ? 'bg-gray-100 font-semibold cursor-default' : ''
          ]"
          :data-testid="`language-option-${lang.code}`"
          @click="switchLanguage(lang.code)"
        >
          <span class="text-lg">{{ lang.flag }}</span>
          <span>{{ lang.name }}</span>
          <svg
            v-if="lang.code === currentLocale"
            class="h-4 w-4 text-blue-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
        </Button>
      </div>
    </div>
    <HelpTextPopover :text="t('vde.languages.selector.help.text')" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import HelpTextPopover from "@/shared/components/HelpTextPopover.vue";
import { Button } from "@/components/ui/button";

const { t, locale } = useI18n();

const dropdownOpen = ref(false);

const languages = [
  { code: "en", name: "English", flag: "\uD83C\uDDFA\uD83C\uDDF8" },
  { code: "de", name: "Deutsch", flag: "\uD83C\uDDE9\uD83C\uDDEA" },
] as const;

const currentLocale = computed(() => locale.value);

const selectedLanguage = computed(() => {
  return languages.find((l) => l.code === currentLocale.value) ?? languages[0];
});

function closeDropdown(): void {
  setTimeout(() => {
    dropdownOpen.value = false;
  }, 150);
}

function switchLanguage(code: string): void {
  // Update Vue i18n locale
  locale.value = code;

  // Set ASP.NET Core culture cookie so server-rendered pages stay in sync
  const cookieValue = encodeURIComponent(`c=${code}|uic=${code}`);
  document.cookie = `.AspNetCore.Culture=${cookieValue};path=/;max-age=31536000;samesite=lax`;

  dropdownOpen.value = false;
}
</script>
